import { glossaryService, GlossaryEntry } from './glossaryService';

export interface KeywordDefinition {
  id: string;
  word: string;
  explanation: string;
  category?: string;
  aliases?: string[];
  image?: string;
}

export interface KeywordConfig {
  keywords: KeywordDefinition[];
  enabled: boolean;
}

class KeywordService {
  private definitions: Map<string, KeywordDefinition> = new Map();
  private enabled: boolean = true;

  constructor() {
    console.log('KeywordService: Constructor called');
    this.loadEnabledState();
    // Load definitions asynchronously
    this.loadDefinitionsFromGlossary().then(() => {
      console.log('KeywordService: Constructor completed, definitions count:', this.definitions.size);
    }).catch((error) => {
      console.error('KeywordService: Error loading definitions:', error);
    });
  }

  private loadEnabledState() {
    // Always enable keyword highlighting for all users
    this.enabled = true;
  }

  private saveEnabledState() {
    localStorage.setItem('keywordFeatureEnabled', JSON.stringify(this.enabled));
  }

  private async loadDefinitionsFromGlossary() {
    const activeEntries = await glossaryService.getActiveEntries();
    
    const keywordDefinitions: KeywordDefinition[] = activeEntries.map(entry => ({
      id: entry.id,
      word: entry.term,
      explanation: entry.definition,
      category: entry.category,
      aliases: entry.aliases,
      image: entry.image
    }));

    this.addDefinitions(keywordDefinitions);
  }

  // Refresh definitions from glossary (call this when glossary is updated)
  public async refreshFromGlossary() {
    this.definitions.clear();
    await glossaryService.refreshFromAPI();
    await this.loadDefinitionsFromGlossary();
  }

  addDefinitions(definitions: KeywordDefinition[]) {
    definitions.forEach(def => {
      this.definitions.set(def.word.toLowerCase(), def);
      
      // Add aliases
      if (def.aliases) {
        def.aliases.forEach(alias => {
          this.definitions.set(alias.toLowerCase(), def);
        });
      }
    });
  }

  getDefinition(word: string): KeywordDefinition | null {
    return this.definitions.get(word.toLowerCase()) || null;
  }

  getAllDefinitions(): KeywordDefinition[] {
    const uniqueDefinitions = new Set<KeywordDefinition>();
    this.definitions.forEach(def => uniqueDefinitions.add(def));
    return Array.from(uniqueDefinitions);
  }

  isEnabled(): boolean {
    return true;
  }

  setEnabled(enabled: boolean) {
    // Keep feature always enabled, but persist the user's preference for future flexibility
    this.enabled = true;
    this.saveEnabledState();
  }

  findKeywordsInText(text: string): Array<{ word: string; definition: KeywordDefinition; startIndex: number; endIndex: number }> {
    console.log('KeywordService: Finding keywords in text:', text.substring(0, 100) + '...');
    console.log('KeywordService: Available definitions:', Array.from(this.definitions.keys()));
    
    // Feature is always enabled
    const foundKeywords: Array<{ word: string; definition: KeywordDefinition; startIndex: number; endIndex: number }> = [];
    
    // Use a more robust approach: search for each keyword definition in the text
    this.definitions.forEach((definition, key) => {
      // Search for the main term
      const regex = new RegExp(`\\b${definition.word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      let match;
      
      while ((match = regex.exec(text)) !== null) {
        foundKeywords.push({
          word: match[0],
          definition,
          startIndex: match.index,
          endIndex: match.index + match[0].length
        });
      }
      
      // Also search for aliases
      if (definition.aliases) {
        definition.aliases.forEach(alias => {
          const aliasRegex = new RegExp(`\\b${alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
          let aliasMatch;
          
          while ((aliasMatch = aliasRegex.exec(text)) !== null) {
            foundKeywords.push({
              word: aliasMatch[0],
              definition,
              startIndex: aliasMatch.index,
              endIndex: aliasMatch.index + aliasMatch[0].length
            });
          }
        });
      }
    });

    // Remove duplicates and sort by start index
    const uniqueKeywords = foundKeywords.filter((keyword, index, self) => 
      index === self.findIndex(k => k.startIndex === keyword.startIndex)
    ).sort((a, b) => a.startIndex - b.startIndex);

    console.log('KeywordService: Total keywords found:', uniqueKeywords.length);
    return uniqueKeywords;
  }

  processTextWithKeywords(text: string): string {
    if (!this.enabled) return text;

    const keywords = this.findKeywordsInText(text);
    if (keywords.length === 0) return text;

    // For now, just return the original text
    // The actual keyword processing will be done in the React components
    return text;
  }
}

export const keywordService = new KeywordService();
export default keywordService;
